import {
  Card,
  CardType,
  createCard,
  calcSubbookBodyCost,
  type SubbookDefinition,
  type SubbookId,
} from "@/program/Card";
import { CardListEngine } from "./CardListEngine";
import type { MagicCirclePattern } from "./MagicCircle";

/** サブブックごとの外観テーマ */
export interface SubbookTheme {
  /** 魔法陣インク色（`--grimoire-ink` CSS変数に反映） */
  inkColor: string;
  /** 魔法陣パターン */
  patternType: MagicCirclePattern;
  /** ラフエッジのseed（荒さ固定・形状のみ変化） */
  edgeSeed: number;
}

/** モーダルオーバーレイのセレクタ（ESC競合回避用） */
const MODAL_OVERLAY_SELECTOR = [
  ".condition-modal-overlay",
  ".step-modal-overlay",
  ".orb-modal-overlay",
  ".turn-modal-overlay",
  ".subbook-selector-modal-overlay",
].join(", ");

/** サブブックのモーダルに渡すMP上限（サブブック自体にはMP制限を設けない） */
const SUBBOOK_NO_MP_LIMIT = 9999;

/**
 * サブブック定義パネル
 *
 * メインプログラムとは別にカードを配置し、サブブック（再利用可能な呪文の組み合わせ）を定義する。
 * ProgramAreaと同じCSSクラスを使用してカードを表示する。
 *
 * 共通のカード操作ロジックは CardListEngine に委譲し、
 * このクラスはアクティブ化管理・コールバック通知などの固有ロジックのみ担う。
 */
export class SubbookPanel {
  private container: HTMLElement;
  private bodyContainer: HTMLElement;
  private engine: CardListEngine;
  private active = false;
  private subbookId: string;
  private panelName: string;

  private onActivateCallback?: () => void;
  private onDeactivateCallback?: () => void;
  private onCostChangeCallback?: (cost: number) => void;

  private handleEscKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape" && this.active) {
      const hasOpenModal = document.querySelector(MODAL_OVERLAY_SELECTOR);
      if (!hasOpenModal) {
        this.deactivate();
      }
    }
  };

  constructor(parentElement: HTMLElement, subbookId: string, name: string) {
    this.subbookId = subbookId;
    this.panelName = name;

    this.container = document.createElement("div");
    this.container.className = "subbook-panel";
    this.container.style.display = "none";

    // スクリーンリーダー向け見出し（視覚的には非表示）
    const heading = document.createElement("h2");
    heading.className = "sr-only";
    heading.textContent = name;
    this.container.appendChild(heading);

    // ボディ
    this.bodyContainer = document.createElement("div");
    this.bodyContainer.className = "subbook-panel__body";
    this.container.appendChild(this.bodyContainer);

    parentElement.appendChild(this.container);

    this.engine = new CardListEngine({
      mimeType: "application/x-questile-subbook",
      rootDropContainer: this.bodyContainer,
      useIconSVG: false,
      onCardsChanged: () => {
        this.notifyCostChange();
      },
      getAvailableMP: () => SUBBOOK_NO_MP_LIMIT,
      getMaxMP: () => SUBBOOK_NO_MP_LIMIT,
      onBranchBodyAddClicked: () => {
        this.activate();
      },
      shouldShowPlaceholder: () =>
        this.engine.cards.length === 0 && !this.active,
      placeholderText: "サブブックは空です",
      renderSuffix: () => {
        const addBtn = document.createElement("button");
        addBtn.className = "subbook-panel__add";
        addBtn.textContent = "呪文を追加";
        addBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          this.engine.setActiveContainerId(null);
          this.activate();
        });
        return [addBtn];
      },
    });

    // ESCキーでアクティブ解除（モーダルが開いていない場合のみ）
    document.addEventListener("keydown", this.handleEscKey);

    this.engine.render();
  }

  show(): void {
    this.container.style.display = "";
  }

  hide(): void {
    this.container.style.display = "none";
    this.deactivate();
  }

  /**
   * 構築済みCardオブジェクトを追加（モーダルで設定済みのカード用）
   * @returns 常にtrue（サブブックにはMP制限なし）
   */
  addCardDirect(card: Card): boolean {
    const activeId = this.engine.getActiveContainerId();
    if (activeId) {
      const branch = this.engine.findBranch(activeId);
      if (branch) {
        this.engine.addCardToBranch(activeId, card);
      } else {
        this.engine.setActiveContainerId(null);
        this.engine.addCardToRoot(card);
      }
    } else {
      this.engine.addCardToRoot(card);
    }
    return true;
  }

  /**
   * カードを追加（アクティブなブランチに追加される）
   * @returns 常にtrue（サブブックにはMP制限なし）
   */
  addCard(type: CardType, subbookId?: SubbookId): boolean {
    const card = createCard(type, undefined, undefined, subbookId);
    const activeId = this.engine.getActiveContainerId();
    if (activeId) {
      const branch = this.engine.findBranch(activeId);
      if (branch) {
        this.engine.addCardToBranch(activeId, card);
      } else {
        // 無効なコンテナ → ルートにフォールバック
        this.engine.setActiveContainerId(null);
        this.engine.addCardToRoot(card);
      }
    } else {
      this.engine.addCardToRoot(card);
    }
    return true;
  }

  /**
   * カードを削除
   */
  removeCard(cardId: string): void {
    this.engine.removeCard(cardId);
  }

  /**
   * 全カードをクリア
   */
  clear(): void {
    this.engine.clear();
  }

  /**
   * サブブック定義を取得
   */
  getDefinition(): SubbookDefinition {
    return {
      id: this.subbookId,
      name: this.panelName,
      cards: [...this.engine.cards],
    };
  }

  /**
   * 現在のコスト合計を取得
   */
  getCurrentCost(): number {
    return calcSubbookBodyCost(this.engine.cards);
  }

  /**
   * アクティブ状態かどうかを取得
   */
  isActive(): boolean {
    return this.active;
  }

  /**
   * アクティブ化（呪文追加モード）
   */
  activate(): void {
    if (this.active) return;
    this.active = true;
    this.bodyContainer.classList.add("subbook-panel__body--active");
    this.engine.render();
    this.onActivateCallback?.();
  }

  /**
   * 非アクティブ化
   */
  deactivate(): void {
    if (!this.active) return;
    this.active = false;
    this.engine.setActiveContainerId(null);
    this.bodyContainer.classList.remove("subbook-panel__body--active");
    this.engine.render();
    this.onDeactivateCallback?.();
  }

  /**
   * 指定カードIDをハイライト（実行中表示）
   */
  highlightCard(cardId: string): void {
    this.engine.highlightCard(cardId);
  }

  /**
   * 全カードのハイライトを解除
   */
  clearHighlight(): void {
    this.engine.clearHighlight();
  }

  /**
   * アクティブ化時コールバックを設定
   */
  onActivate(cb: () => void): void {
    this.onActivateCallback = cb;
  }

  /**
   * 非アクティブ化時コールバックを設定
   */
  onDeactivate(cb: () => void): void {
    this.onDeactivateCallback = cb;
  }

  /**
   * コスト変更時コールバックを設定
   */
  onCostChange(cb: (cost: number) => void): void {
    this.onCostChangeCallback = cb;
  }

  /**
   * ターゲットモード: アクティブコンテナを設定
   */
  setActiveContainer(branchId: string | null): void {
    this.engine.setActiveContainerId(branchId);
    if (branchId) {
      this.activate();
    }
    this.engine.render();
  }

  /**
   * カード移動（テスト用publicインターフェース）
   */
  moveCardById(
    draggedId: string,
    targetId: string,
    position: "before" | "after"
  ): void {
    this.engine.moveCard(draggedId, targetId, position);
  }

  /**
   * カードをブランチに移動（テスト用publicインターフェース）
   */
  moveCardToBranchById(
    draggedId: string,
    branchId: string,
    index?: number
  ): void {
    this.engine.moveCardToBranch(draggedId, branchId, index);
  }

  /**
   * カードをルートに移動（テスト用publicインターフェース）
   */
  moveCardToRootById(draggedId: string, index?: number): void {
    this.engine.moveCardToRoot(draggedId, index);
  }

  /**
   * WAI-ARIA タブパネルとして設定する
   */
  configureTabPanel(id: string, labelledBy: string): void {
    this.container.id = id;
    this.container.setAttribute("role", "tabpanel");
    this.container.setAttribute("aria-labelledby", labelledBy);
  }

  /**
   * インスタンスを破棄する（イベントリスナーを解除）
   */
  destroy(): void {
    document.removeEventListener("keydown", this.handleEscKey);
  }

  // ─── 内部メソッド ───

  private notifyCostChange(): void {
    this.onCostChangeCallback?.(this.getCurrentCost());
  }
}
